package net.dipgo.game

import net.dipgo.game.board.BoardPosition

interface GameLogic {
    fun groupForPoint(point: Point): Set<Point>
    fun getAdjacentPoints(point: Point): List<Point>
    fun countLibertiesForGroupAtPoint(point: Point): Int
    fun makeMove(colour: PointState, point: Point): Boolean
}

data class Point(val xCoord: Int, val yCoord: Int) {
    companion object {
        fun fromMove(move: String): Point {
            val xCoord = move.substringBefore(",")
            val yCoord = move.substringAfter(",")
            return Point(xCoord.toInt(), yCoord.toInt())
        }
    }
}

class GameLogicImp(val boardPosition: BoardPosition) : GameLogic {

    override fun groupForPoint(point: Point): Set<Point> {
        // O(k) flood fill: each connected same-colour point is visited exactly once.
        // Returns the same set the previous O(k²) implementation did, including for
        // EMPTY seeds (used by territory scoring).
        val colour = boardPosition.getStateOfPoint(point.xCoord, point.yCoord)
        val group = mutableSetOf(point)
        val stack = ArrayDeque<Point>()
        stack.addLast(point)

        while (stack.isNotEmpty()) {
            val current = stack.removeLast()
            for (adjacent in getAdjacentPoints(current)) {
                if (boardPosition.getStateOfPoint(adjacent.xCoord, adjacent.yCoord) == colour &&
                    group.add(adjacent)
                ) {
                    stack.addLast(adjacent)
                }
            }
        }

        return group
    }

    override fun getAdjacentPoints(point: Point): List<Point> {
        val allAdjacentPoints = listOf(
            Point(point.xCoord - 1, point.yCoord),
            Point(point.xCoord, point.yCoord - 1),
            Point(point.xCoord + 1, point.yCoord),
            Point(point.xCoord, point.yCoord + 1)
        )

        return allAdjacentPoints.filter { it.xCoord >= 0 && it.yCoord >= 0 && it.xCoord < boardPosition.xSize && it.yCoord < boardPosition.ySize }
    }

    override fun countLibertiesForGroupAtPoint(point: Point): Int {
        val liberties: MutableSet<Point> = mutableSetOf()
        val group = groupForPoint(point)
        group.forEach {
            liberties.addAll(getAdjacentPoints(it).filter { adjacentPoint ->
                boardPosition.getStateOfPoint(
                    adjacentPoint.xCoord,
                    adjacentPoint.yCoord
                ) == PointState.EMPTY
            })
        }
        return liberties.size
    }

    override fun makeMove(colour: PointState, point: Point): Boolean {
        if (!boardPosition.placeStone(colour, point.xCoord, point.yCoord)) {
            throw IllegalArgumentException("Invalid move: space occupied or not within boundaries of board!")
        }

        removeAdjacentCapturedGroups(point)

        if (countLibertiesForGroupAtPoint(point) == 0) {
            boardPosition.removeStone(point.xCoord, point.yCoord)
            return false
        }

        return true
    }

    private fun removeAdjacentCapturedGroups(point: Point) {
        val pointsWhichCouldPotentiallyBelongToCapturedGroups: List<Point> = getAdjacentPoints(point).filter {
            boardPosition.getStateOfPoint(it.xCoord, it.yCoord) != PointState.EMPTY &&
                    boardPosition.getStateOfPoint(it.xCoord, it.yCoord) != boardPosition.getStateOfPoint(
                point.xCoord,
                point.yCoord
            )
        }

        pointsWhichCouldPotentiallyBelongToCapturedGroups.filter {
            countLibertiesForGroupAtPoint(it) == 0
        }.forEach {
            removeGroupContainingThisPointFromBoard(it)
        }
    }

    private fun removeGroupContainingThisPointFromBoard(point: Point) {
        groupForPoint(point).forEach { boardPosition.removeStone(it.xCoord, it.yCoord) }
    }
}